/**
 * Flow transition table — the single source of truth for both the frontend
 * flow graph (flowGraph.js) and the DB validator (validate_flow_transition).
 *
 * A "Leg" is the repeating unit shared by rides and packages:
 *   meeting_proposed -> meeting_agreed -> waiting -> both_present -> handoff
 * with a no-show / dispute branch off `waiting`.
 *
 *   Ride    = requested -> confirmed -> [Leg] -> completed -> review
 *   Package = requested -> confirmed -> [Leg: pickup] -> [Leg: delivery] -> completed
 *
 * Arrival is a two-party handshake that separates "I arrived" from "we're both here":
 *   first marks here -> `waiting` (wait-timer + no-show live here); the other marks
 *   here -> `both_arrived`. In `both_arrived` EACH party must actively confirm the
 *   other is present (`confirm_presence`, first self-loops, second -> `both_present`)
 *   OR `dispute_presence` (-> disputed_no_show). This stops a false "I'm here" from
 *   auto-completing when the second person shows up. Handoff mirrors the confirm step:
 *   first confirms -> `handoff`; the other confirms -> `completed`. Guards that check
 *   "the OTHER party already acted" (other_confirmed_presence / other_confirmed) make
 *   these idempotent per party — the acting user's own mark/confirm is applied by the
 *   same advance_flow call, so the guard reads the counterpart's state.
 *
 * Roles are entity-agnostic contextual ACTORS the adapter resolves per row:
 *   'either'    — any conversation participant
 *   'recipient' — the request recipient (ride/package owner)
 *   'accused'   — the party recorded in no_show_party (reported absent)
 *   'system'    — server only (pg_cron / RPC); never offered to a client user
 *
 * A package pickup leg reaching `handoff` advances to the delivery leg
 * (advance_leg: handoff -> meeting_proposed, current_leg='delivery') instead of
 * completing. Rides and package delivery legs complete at `handoff -> completed`.
 */

export const STATES = [
  'requested',
  'confirmed',
  'meeting_proposed',
  'meeting_agreed',
  'waiting',
  'both_arrived',
  'both_present',
  'handoff',
  'completed',
  'cancelled',
  'declined',
  'no_show',
  'disputed_no_show',
  'mutual_no_show',
];

export const TERMINAL_STATES = ['completed', 'cancelled', 'declined', 'no_show', 'mutual_no_show'];

// state -> card component id (see src/components/Chat/flow/cards/)
export const CARD_FOR = {
  requested: 'RequestCard',
  confirmed: 'MeetingCard',
  meeting_proposed: 'MeetingCard',
  meeting_agreed: 'ArrivalCard',
  waiting: 'WaitingCard',
  both_arrived: 'ConfirmPresenceCard',
  both_present: 'HandoffCard',
  handoff: 'HandoffCard',
  disputed_no_show: 'NoShowCard',
};

// A package pickup leg's handoff redirects to the delivery leg instead of terminating.
export const PACKAGE_SECOND_LEG_FROM = 'handoff';

export const TRANSITIONS = [
  { action: 'request', from: 'requested', to: 'confirmed', roles: 'recipient', guard: null },
  { action: 'decline', from: 'requested', to: 'declined', roles: 'recipient', guard: null },

  { action: 'propose_time', from: 'confirmed', to: 'meeting_proposed', roles: 'either', guard: null },
  { action: 'propose_time', from: 'meeting_proposed', to: 'meeting_proposed', roles: 'either', guard: null },
  { action: 'agree_time', from: 'meeting_proposed', to: 'meeting_agreed', roles: 'either', guard: 'not_proposer' },

  { action: 'mark_here', from: 'meeting_agreed', to: 'waiting', roles: 'either', guard: null },
  // Second arrival records presence only (no auto-confirm) — lands in both_arrived,
  // where each party must actively confirm the other is present, or dispute.
  // Guarded to the not-yet-arrived party so the one already waiting can't advance solo.
  { action: 'mark_here', from: 'waiting', to: 'both_arrived', roles: 'either', guard: 'not_i_arrived' },

  // Mutual presence confirmation: each party confirms the OTHER is here. First
  // confirmer self-loops; the second completes to both_present. Either can dispute.
  { action: 'confirm_presence', from: 'both_arrived', to: 'both_arrived', roles: 'either', guard: 'not_other_confirmed_presence' },
  { action: 'confirm_presence', from: 'both_arrived', to: 'both_present', roles: 'either', guard: 'other_confirmed_presence' },
  { action: 'dispute_presence', from: 'both_arrived', to: 'disputed_no_show', roles: 'either', guard: null },
  { action: 'cancel', from: 'both_arrived', to: 'cancelled', roles: 'either', guard: null },

  { action: 'confirm_handoff', from: 'both_present', to: 'handoff', roles: 'either', guard: null },
  { action: 'confirm_handoff', from: 'handoff', to: 'completed', roles: 'either', guard: 'other_confirmed' },
  { action: 'advance_leg', from: 'handoff', to: 'meeting_proposed', roles: 'either', guard: 'other_confirmed' },

  { action: 'report_no_show', from: 'waiting', to: 'disputed_no_show', roles: 'either', guard: 'i_arrived' },
  { action: 'admit_no_show', from: 'disputed_no_show', to: 'no_show', roles: 'accused', guard: null },
  // Disputing no longer escapes to a clean 'cancelled'. It resolves to a no-fault
  // 'mutual_no_show' (recorded, both parties soft-flagged) — closes the impunity gap
  // while not auto-penalizing a genuinely-contested case that has no evidence yet.
  { action: 'dispute_no_show', from: 'disputed_no_show', to: 'mutual_no_show', roles: 'accused', guard: null },
  { action: 'resolve_no_show', from: 'disputed_no_show', to: 'no_show', roles: 'system', guard: 'appeal_window_expired' },

  { action: 'cancel', from: 'confirmed', to: 'cancelled', roles: 'either', guard: null },
  { action: 'cancel', from: 'meeting_proposed', to: 'cancelled', roles: 'either', guard: null },
  { action: 'cancel', from: 'meeting_agreed', to: 'cancelled', roles: 'either', guard: null },
  // In `waiting`, a clean cancel is allowed UNLESS you're the absent party bailing
  // while the other is already there past the meeting time — then it's your no-show.
  { action: 'cancel', from: 'waiting', to: 'cancelled', roles: 'either', guard: 'not_late_cancel' },
  { action: 'cancel_as_no_show', from: 'waiting', to: 'no_show', roles: 'either', guard: 'is_late_cancel' },

  { action: 'mark_done', from: 'confirmed', to: 'completed', roles: 'either', guard: 'travel_date_passed' },
  { action: 'mark_done', from: 'meeting_agreed', to: 'completed', roles: 'either', guard: 'travel_date_passed' },
  { action: 'mark_done', from: 'waiting', to: 'completed', roles: 'either', guard: 'travel_date_passed' },
];
